import type { DrillLocation, ExplorationSecondary, ExplorationVolume, ViewMode } from '../../types/exploration';
import { resolveDrillScope } from '../drill/resolveDrillScope';
import { clusterTopForVolume, clusterTopKeyString, isDrillLocationEmpty } from '../drill/drillLocation';
import { volumeDisplayName } from '../formatBytes';
import { accountKey } from '../hierarchy/buildClusterTopLevel';
import { effectiveSecondary, isEffectiveRegionOrTagSecondary } from '../secondaryGrouping';
import { reasonLabelForVolume } from './focusedReasonHelpers';
import { isInstanceDrillLevel } from './sidebarPalettes';
import { supportStyleStatusLabel, volumeStatusLabel } from './volumeStatusLabel';

export interface TopLevelGroup {
  name: string;
  vols: ExplorationVolume[];
  count: number;
  totalSize: number;
}

export interface ComputeTopLevelGroupsOptions {
  secondary: ExplorationSecondary;
  location: DrillLocation;
  groupByAccount: boolean;
  tagKey: string;
  view?: ViewMode;
}

function bucketVolumes(
  vols: ExplorationVolume[],
  getKey: (v: ExplorationVolume) => string
): TopLevelGroup[] {
  const m = new Map<string, TopLevelGroup>();
  vols.forEach((v) => {
    const k = getKey(v);
    if (!m.has(k)) m.set(k, { name: k, vols: [], count: 0, totalSize: 0 });
    const e = m.get(k)!;
    e.vols.push(v);
    e.count += 1;
    e.totalSize += v.size || 0;
  });
  return [...m.values()].sort((a, b) => b.totalSize - a.totalSize);
}

export function computeTopLevelGroups(
  volumes: ExplorationVolume[],
  opts: ComputeTopLevelGroupsOptions
): TopLevelGroup[] {
  const view = opts.view ?? 'exploration';
  const groupingSecondary = effectiveSecondary(opts.secondary, opts.tagKey);
  const resolveScope = () =>
    resolveDrillScope(volumes, {
      secondary: opts.secondary,
      location: opts.location,
      groupByAccount: opts.groupByAccount,
      tagKey: opts.tagKey,
      view,
    });

  // No selectedVolumeId check needed here (unlike instanceChildrenForSelection
  // in the hierarchy builder): this function's only caller never renders while
  // a volume is selected — see `detailOnly` in ExplorationSidebar.tsx.
  if (isInstanceDrillLevel(opts.location)) {
    return bucketVolumes(resolveScope().volumes, (v) => volumeStatusLabel(v, view) || '(unknown)');
  }

  if (opts.groupByAccount && isDrillLocationEmpty(opts.location)) {
    return bucketVolumes(volumes, accountKey);
  }

  // Drilled to exactly a focused Unsupported category — no reason breakdown,
  // straight to its volumes (one group per volume). Bucket by ebsId, not
  // display name — display names aren't guaranteed unique across volumes.
  if (opts.location.focusedActivationKey === 'unsupported' && !opts.location.reasonLabel) {
    return bucketVolumes(resolveScope().volumes, (v) => v.ebsId).map((g) => ({
      ...g,
      name: volumeDisplayName(g.vols[0]) || g.name,
    }));
  }

  // Drilled to exactly a focused Pending-setup category with no reason chosen
  // yet — bucket by reason within it.
  if (opts.location.focusedActivationKey === 'potentiallySupported' && !opts.location.reasonLabel) {
    const focusedKey = opts.location.focusedActivationKey;
    return bucketVolumes(resolveScope().volumes, (v) => reasonLabelForVolume(v, focusedKey));
  }

  const resolved = resolveScope();
  const vols = resolved.volumes;
  const { structuralPath } = resolved;

  if (structuralPath.length === 0) {
    if (groupingSecondary === 'cluster') {
      return bucketVolumes(vols, (v) => clusterTopKeyString(clusterTopForVolume(v)));
    }
    if (groupingSecondary === 'region') {
      return bucketVolumes(vols, (v) => v.region || '(unknown)');
    }
    if (groupingSecondary === 'tag') {
      return bucketVolumes(
        vols,
        (v) => (v.tags && opts.tagKey && v.tags[opts.tagKey]) || '(untagged)'
      );
    }
    if (groupingSecondary === 'none') {
      return bucketVolumes(vols, (v) => volumeStatusLabel(v, view) || '(unknown)');
    }
    return [];
  }

  const top = structuralPath[0];
  let getKey: ((v: ExplorationVolume) => string) | undefined;

  if (groupingSecondary === 'cluster' && (top === 'EC2 Instances' || top === 'Unattached')) {
    getKey =
      view === 'activation'
        ? (v) => volumeStatusLabel(v, view) || '(unknown)'
        : top === 'Unattached'
          ? (v) => supportStyleStatusLabel(v, view) || '(unknown)'
          : (v) => v.instanceId || '(no instance)';
  } else if (isEffectiveRegionOrTagSecondary(opts.secondary, opts.tagKey)) {
    getKey = (v) => volumeStatusLabel(v, view) || '(unknown)';
  } else if (groupingSecondary === 'cluster') {
    if (structuralPath.length === 1) {
      getKey = (v) => v.nodeGroup || '(No Group)';
    } else if (structuralPath.length === 2) {
      getKey = (v) => v.instanceId || '(no instance)';
    }
  }

  if (!getKey) return [];
  return bucketVolumes(vols, getKey);
}
